import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Renderer2,
} from '@angular/core';

@Directive({
  selector: '[appTilt]',
  standalone: true,
})
export class TiltDirective implements OnDestroy {
  @Input('appTiltMax') public maxTilt = 10;
  @Input('appTiltScale') public scale = 1.02;
  @Input('appTiltTransition') public transition = 'transform 0.25s ease';
  @Input('appTiltGlare') public glare = true;
  @Input('appTiltPerspective') public perspective = 1200;

  private readonly element: HTMLElement;
  private glareEl: HTMLElement | null = null;
  private pointerActive = false;

  public constructor(
    host: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2
  ) {
    this.element = host.nativeElement;
    this.setup();
  }

  @HostListener('pointermove', ['$event'])
  public onPointerMove(event: PointerEvent): void {
    if (!this.element) {
      return;
    }
    this.pointerActive = true;
    const rect = this.element.getBoundingClientRect();
    const relativeX = (event.clientX - rect.left) / rect.width;
    const relativeY = (event.clientY - rect.top) / rect.height;
    const rotateY = (relativeX - 0.5) * this.maxTilt;
    const rotateX = (0.5 - relativeY) * this.maxTilt;
    this.applyTransform(rotateX, rotateY, this.scale);
    this.updateGlare(relativeX, relativeY);
  }

  @HostListener('pointerleave')
  public onPointerLeave(): void {
    this.pointerActive = false;
    this.applyTransform(0, 0, 1);
    this.resetGlare();
  }

  @HostListener('pointerenter')
  public onPointerEnter(): void {
    this.applyTransform(0, 0, this.scale);
  }

  public ngOnDestroy(): void {
    this.resetGlare();
  }

  private setup(): void {
    this.renderer.setStyle(this.element, 'transformStyle', 'preserve-3d');
    this.renderer.setStyle(
      this.element,
      'transition',
      this.transition
    );
    this.renderer.setStyle(this.element, 'willChange', 'transform');
    this.glareEl = this.element.querySelector('[data-tilt-glare]') as HTMLElement | null;
    if (this.glareEl) {
      this.renderer.setStyle(this.glareEl, 'pointerEvents', 'none');
    }
  }

  private applyTransform(rotateX: number, rotateY: number, scale: number): void {
    const transform = `perspective(${this.perspective}px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(${scale})`;
    this.renderer.setStyle(this.element, 'transform', transform);
  }

  private updateGlare(relativeX: number, relativeY: number): void {
    if (!this.glare || !this.glareEl || !this.pointerActive) {
      return;
    }
    const glareAngle = Math.atan2(relativeY - 0.5, relativeX - 0.5) * (180 / Math.PI) + 90;
    const intensity = Math.max(0.15, 0.5 - Math.abs(relativeY - 0.5));
    this.renderer.setStyle(
      this.glareEl,
      'background',
      `linear-gradient(${glareAngle}deg, rgba(255, 255, 255, ${intensity}) 0%, transparent 60%)`
    );
  }

  private resetGlare(): void {
    if (this.glareEl) {
      this.renderer.setStyle(this.glareEl, 'background', 'none');
    }
  }
}
